import React, { useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  useColorModeValue,
  Divider,
  Button,
  SimpleGrid,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { FaWallet } from 'react-icons/fa'
import { MdDataExploration } from 'react-icons/md'
import { useAccount, useConnect } from 'wagmi'

import { truncateAddress } from '../utils/helpers'
import SignMessageForm from '../components/SignMessageForm'
import useAuth from '../hooks/useAuth'
import metamaskIcon from '../assets/metamask.png'
import coinbaseIcon from '../assets/coinbase.png'
import walletconnectIcon from '../assets/walletConnect.png'
import logo from '../assets/logo.png'

const LoginPage: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const backgroundColor = useColorModeValue('gray.50', 'gray.800')
  const boxBg = useColorModeValue('white', 'gray.700')

  const { address, isConnected } = useAccount()
  const { connectAsync, connectors } = useConnect()
  const { isAuthenticated, isLoading, error, signAndAuthenticate } = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/data')
    }
  }, [isAuthenticated, navigate])

  const handleConnect = async (connector: any) => {
    try {
      await connectAsync({ connector })
      onClose()
    } catch (e) {
      console.error(e)
    }
  }

  // Handle successful authentication
  const handleSignMessage = async () => {
    const success = await signAndAuthenticate()
    if (success) {
      navigate('/data')
    }
  }

  const getConnectorIcon = (connector: any) => {
    switch (connector.id) {
      case 'metaMaskSDK':
        return metamaskIcon
      case 'coinbaseWalletSDK':
        return coinbaseIcon
      case 'walletConnect':
        return walletconnectIcon
      default:
        return connector.icon
    }
  }

  return (
    <Box bg={backgroundColor} minH="100vh" py="12">
      <Container
        maxW="lg"
        py={{ base: '12', md: '20' }}
        px={{ base: '0', sm: '8' }}
      >
        <Box
          bg={boxBg}
          py="8"
          px={{ base: '4', sm: '10' }}
          shadow="base"
          rounded={{ sm: 'lg' }}
        >
          <VStack spacing="6" align="center">
            <Image src={logo} alt="logo" />

            {!isConnected ? (
              <>
                <Text textAlign="center" color="gray.500">
                  Connect your wallet to access your crypto data analytics.
                </Text>

                <Button
                  leftIcon={<FaWallet />}
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  onClick={onOpen}
                >
                  Connect Wallet
                </Button>
              </>
            ) : (
              <>
                <Box
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  border="1px"
                  borderColor="gray.200"
                  width="full"
                >
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Connected Wallet
                    </Text>
                    <Text fontWeight="bold">
                      {truncateAddress(address || '')}
                    </Text>
                  </VStack>
                </Box>

                <Divider />

                <SignMessageForm
                  // @ts-expect-error assignable
                  onSign={handleSignMessage}
                  isLoading={isLoading}
                  error={error}
                />
              </>
            )}
          </VStack>
        </Box>
      </Container>

      {/* Wallet Connection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay zIndex={80} />
        <ModalContent
          containerProps={{
            zIndex: 80,
          }}
        >
          <ModalHeader>Connect Wallet</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <SimpleGrid columns={1} spacing={3}>
              {connectors.map((connector) => {
                if (connector.id === 'injected') return null
                const connectorIcon = getConnectorIcon(connector)
                return (
                  <Button
                    key={connector.id}
                    leftIcon={
                      <Image src={connectorIcon} alt={connector.name} w={6} />
                    }
                    onClick={() => handleConnect(connector)}
                    // isDisabled={!connector.ready}
                    loadingText="Connecting"
                    variant="outline"
                    justifyContent="flex-start"
                    height="60px"
                    fontSize="md"
                  >
                    {connector.id === 'metaMask'
                      ? 'MetaMask'
                      : connector.id === 'coinbaseWallet'
                        ? 'Coinbase Wallet'
                        : connector.id === 'walletConnect'
                          ? 'WalletConnect'
                          : connector.name}
                  </Button>
                )
              })}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default LoginPage
